import './AdminConsole.css';
import adminService from '../Services/adminServices';
import { useState, useEffect } from 'react';
import { FaEye, FaEyeSlash, FaUsers, FaFileInvoiceDollar, FaUserShield, FaChartLine, FaXmark, FaPlus, FaPenToSquare, FaTrash, FaBuilding } from 'react-icons/fa6';
import LogoAnimated from '../Refs/LogoAminated.gif';

// ========== API INTERACTIONS ==========

//filterObject = {name, createdFromDate (date), createdToDate (date), lastModifiedFrom (date), lastModifiedTo (date), active, city, state, country, billingEmail, billingPhone, paymentMethod, trial, owner}
/*
return {
        id: tenant.id,
        name: tenant.name,
        createdOn: tenant.created_on,
        lastModifiedOn: tenant.last_modified_on,
        active: tenant.active,
        website: tenant.website,
        billingAddress: tenant.billing_address,
        billingEmails: tenant.billing_emails,
        billingPhone: tenant.billing_phone,
        pastBillings: tenant.past_billings,
        paymentMethod: tenant.payment_method,
        trial: tenant.trial
    };
*/
const fetchTenants = async (apiToken, filterObject, setSuccess, setFailure) => {
    try {
        const response = await adminService.getTenants(apiToken, filterObject);
        if(response.status >= 200 && response.status < 300) {
            console.log('Tenants fetched:', response.data);
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        console.error("Error fetching tenants:", error);
    }
};

//filterObject = {name, email, phone, lastModifiedFrom (date), lastModifiedTo (date), createdOnFrom (date), createdOnTo (date), emailVerifiedFrom (date), emailVerifiedTo (date), deletedFrom (date), deletedTo (date), active, tenant}
/*
return {
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      phone: user.phone,
      subscription: user.subscription,
      lastModifiedOn: user.last_modified_on,
      createdOn: user.created_on,
      emailVerifiedOn: user.email_verified_on,
      deletedOn: user.deleted_on,
      active: user.active,
      accessToken: user.access_token
  };
*/
const fetchUsers = async (apiToken, filterObject, setSuccess, setFailure) => {
    try {
        const response = await adminService.getUsers(apiToken, filterObject);
        if(response.status >= 200 && response.status < 300) {
            console.log('Users fetched:', response.data);
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        console.error("Error fetching users:", error);
    }
};

//filterObject = {invoiceDateFrom (date), invoiceDateTo (date), amount, tenant, invoiceNumber, paid, paidDate, dueDate, paymentMethod}
/*
return {
    id: billing.id,
    tenant: billing.tenant,
    period: billing.period,
    amount: billing.amount,
    invoiceNumber: billing.invoice_number,
    invoiceDate: billing.invoice_date
  }
*/
const fetchTenantBillings = async (apiToken, filterObject, setSuccess, setFailure) => {
    try {
        const response = await adminService.getTenantBillings(apiToken, filterObject);
        if(response.status >= 200 && response.status < 300) {
            console.log('Tenant billings fetched:', response.data);
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        }; 
    } catch(error) {
        console.error("Error fetching billings:", error);
    }
};

//filterObject = {name, email, phone, permissions}
/*
return {
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      phone: user.phone,
      subscription: user.subscription,
      lastModifiedOn: user.last_modified_on,
      createdOn: user.created_on,
      createdBy: user.created_by,
      accessToken: user.access_token
  };
*/
const getAdminUsers = async (apiToken, filterObject, setSuccess, setFailure) => {
    try {
        const response = await adminService.getAdminUsers(apiToken, filterObject);
        if(response.status >= 200 && response.status < 300) {
            console.log('Users fetched:', response.data);
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        }; 
    } catch(error) {
        console.error("Error fetching users:", error);
    }
};

const login = async (email, password, handleSuccessfulLogin, handleFailedLogin) => {
    try {
        const response = await adminService.login(email, password);
        if(response.status >= 200 && response.status < 300) {
            console.log('Login successful:', response.data);
            handleSuccessfulLogin(response.data);
        } else {
            handleFailedLogin(`Error: Received status code ${response.status}`);
        };
    } catch(error) {
        console.error("Error logging in:", error);
        handleFailedLogin(error);
    }
};

// userObject = name, email, phone, password, permissions: { tenants: boolean, billings: boolean, users: boolean, sales: boolean }
const createUser = async (apiToken, userObject, setSuccess, setFailure) => {
   try {
        const response = await adminService.createUser(apiToken, userObject);
        if(response.status >= 200 && response.status < 300) {
            console.log('User created:', response.data);
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        }; 
    } catch(error) {
        console.error("Error creating user:", error);
    }
};

// userObject = name, email, phone, password, active, permissions: { tenants: boolean, billings: boolean, users: boolean, sales: boolean }
const updateUser = async (apiToken, userId, userObject, setSuccess, setFailure) => {
    try {
        const response = await adminService.updateUser(apiToken, userId, userObject);
        if(response.status >= 200 && response.status < 300) {
            console.log('User updated:', response.data);
            setSuccess(response.data);
        } else {
            setFailure(`Error: Received status code ${response.status}`);
        }; 
    } catch(error) {
        console.error("Error updating user:", error);
    }
};

// ========== COMPONENTS ==========

const AdminLogin = ({ handleLogin, loginError }) => {
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        await handleLogin(credentials.email, credentials.password);
        setIsLoading(false);
    };

    const checkCredentialValidity = (credential) => {
        if (!credential.email || !credential.password) return false;
        if (credential.email.length < 5) return false;
        if (credential.password.length < 6) return false;
        return true;
    };

    return (
        <div className="admin-login">
            <div className="login-card">
                <div className="login-header">
                    <h1>Admin Console</h1>
                    <p>Sign in to access the admin panel</p>
                </div>
                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            value={credentials.email}
                            onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                            placeholder="Enter your email address"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={credentials.password}
                                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                                placeholder="Enter your password"
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>
                    {loginError && <div className="error-message">{loginError}</div>}
                    <button type="submit" className="btn-primary sign-in-btn" disabled={isLoading || !checkCredentialValidity(credentials)}>
                        {isLoading ? 'Signing In...' : 'Sign In'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const AdminHeader = ({ userData, selectedSection, setSelectedSection, permissions, onLogout }) => {
    const [showDropdown, setShowDropdown] = useState(false);

    const sections = [];
    if (permissions.tenants) sections.push({ key: 'tenants', label: 'Tenants', icon: <FaBuilding /> });
    if (permissions.billings) sections.push({ key: 'billings', label: 'Billings', icon: <FaFileInvoiceDollar /> });
    if (permissions.users) sections.push({ key: 'users', label: 'Users', icon: <FaUserShield /> });
    if (permissions.sales) sections.push({ key: 'sales', label: 'Sales', icon: <FaChartLine /> });

    const getInitials = () => {
        const firstName = userData?.firstName || '';
        const lastName = userData?.lastName || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    return (
        <div className="admin-header">
            <div className="admin-header-left">
                <img src={LogoAnimated} alt="Sofie AI Logo" className="admin-logo" />
                <div className="logo-text-container">
                    <div className="logo-text-line-1">Sofie</div>
                    <div className="logo-text-line-2">Admin Console</div>
                </div>
            </div>
            <div className="admin-header-right">
                {sections.length > 0 && (
                    <select
                        className="section-selector"
                        value={selectedSection}
                        onChange={(e) => setSelectedSection(e.target.value)}
                    >
                        {sections.map((section) => (
                            <option key={section.key} value={section.key}>
                                {section.label}
                            </option>
                        ))}
                    </select>
                )}
                <div className="user-info">
                    <span className="user-name">{userData?.firstName} {userData?.lastName}</span>
                    <div className="user-avatar" onClick={() => setShowDropdown(!showDropdown)}>
                        {getInitials()}
                    </div>
                    {showDropdown && (
                        <div className="user-dropdown">
                            <button className="dropdown-item" onClick={onLogout}>
                                <span>Log Out</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TenantsScreen = ({ accessToken }) => {
    const [tenants, setTenants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterObject, setFilterObject] = useState({});

    useEffect(() => {
        loadTenants();
    }, [accessToken]);

    const loadTenants = async () => {
        setIsLoading(true);
        setError(null);
        await fetchTenants(
            accessToken,
            filterObject,
            (data) => {
                setTenants(data);
                setIsLoading(false);
            },
            (err) => {
                setError(err);
                setIsLoading(false);
            }
        );
    };

    if (isLoading) {
        return <div className="loading-state">Loading tenants...</div>;
    }

    if (error) {
        return <div className="error-state">Error: {error}</div>;
    }

    return (
        <div className="screen-content">
            <div className="screen-header">
                <h2>Tenants</h2>
                <p>Manage all tenant accounts</p>
            </div>
            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Created On</th>
                            <th>Status</th>
                            <th>Website</th>
                            <th>Billing Email</th>
                            <th>Payment Method</th>
                            <th>Trial</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tenants.length > 0 ? (
                            tenants.map((tenant) => (
                                <tr key={tenant.id}>
                                    <td>{tenant.name}</td>
                                    <td>{new Date(tenant.createdOn).toLocaleDateString()}</td>
                                    <td>
                                        <span className={`status-badge ${tenant.active ? 'active' : 'inactive'}`}>
                                            {tenant.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>{tenant.website || '-'}</td>
                                    <td>{tenant.billingEmails || '-'}</td>
                                    <td>{tenant.paymentMethod || '-'}</td>
                                    <td>{tenant.trial ? 'Yes' : 'No'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="no-data">No tenants found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const BillingsScreen = ({ accessToken }) => {
    const [billings, setBillings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterObject, setFilterObject] = useState({});

    useEffect(() => {
        loadBillings();
    }, [accessToken]);

    const loadBillings = async () => {
        setIsLoading(true);
        setError(null);
        await fetchTenantBillings(
            accessToken,
            filterObject,
            (data) => {
                setBillings(data);
                setIsLoading(false);
            },
            (err) => {
                setError(err);
                setIsLoading(false);
            }
        );
    };

    if (isLoading) {
        return <div className="loading-state">Loading billings...</div>;
    }

    if (error) {
        return <div className="error-state">Error: {error}</div>;
    }

    return (
        <div className="screen-content">
            <div className="screen-header">
                <h2>Billings</h2>
                <p>View all tenant billing information</p>
            </div>
            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Invoice Number</th>
                            <th>Tenant</th>
                            <th>Period</th>
                            <th>Amount</th>
                            <th>Invoice Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {billings.length > 0 ? (
                            billings.map((billing) => (
                                <tr key={billing.id}>
                                    <td>{billing.invoiceNumber}</td>
                                    <td>{billing.tenant}</td>
                                    <td>{billing.period}</td>
                                    <td>${billing.amount}</td>
                                    <td>{new Date(billing.invoiceDate).toLocaleDateString()}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="no-data">No billings found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const UsersScreen = ({ accessToken }) => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterObject, setFilterObject] = useState({});
    const [showUserModal, setShowUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    useEffect(() => {
        loadUsers();
    }, [accessToken]);

    const loadUsers = async () => {
        setIsLoading(true);
        setError(null);
        await getAdminUsers(
            accessToken,
            filterObject,
            (data) => {
                setUsers(data);
                setIsLoading(false);
            },
            (err) => {
                setError(err);
                setIsLoading(false);
            }
        );
    };

    const handleCreateUser = () => {
        setEditingUser(null);
        setShowUserModal(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setShowUserModal(true);
    };

    const handleCloseModal = () => {
        setShowUserModal(false);
        setEditingUser(null);
    };

    const handleSaveUser = async (userData) => {
        if (editingUser) {
            await updateUser(
                accessToken,
                editingUser.id,
                userData,
                () => {
                    loadUsers();
                    handleCloseModal();
                },
                (err) => setError(err)
            );
        } else {
            await createUser(
                accessToken,
                userData,
                () => {
                    loadUsers();
                    handleCloseModal();
                },
                (err) => setError(err)
            );
        }
    };

    if (isLoading) {
        return <div className="loading-state">Loading admin users...</div>;
    }

    if (error) {
        return <div className="error-state">Error: {error}</div>;
    }

    return (
        <div className="screen-content">
            <div className="screen-header">
                <h2>Admin Users</h2>
                <p>Manage admin console users and permissions</p>
                <button className="btn-primary" onClick={handleCreateUser}>
                    <FaPlus /> Create New User
                </button>
            </div>
            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Created On</th>
                            <th>Permissions</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length > 0 ? (
                            users.map((user) => (
                                <tr key={user.id}>
                                    <td>{user.firstName} {user.lastName}</td>
                                    <td>{user.email}</td>
                                    <td>{user.phone || '-'}</td>
                                    <td>{new Date(user.createdOn).toLocaleDateString()}</td>
                                    <td>
                                        <div className="permissions-list">
                                            {user.permissions?.tenants && <span className="permission-badge">Tenants</span>}
                                            {user.permissions?.billings && <span className="permission-badge">Billings</span>}
                                            {user.permissions?.users && <span className="permission-badge">Users</span>}
                                            {user.permissions?.sales && <span className="permission-badge">Sales</span>}
                                        </div>
                                    </td>
                                    <td>
                                        <button className="action-btn edit-btn" onClick={() => handleEditUser(user)}>
                                            <FaPenToSquare />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="no-data">No admin users found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {showUserModal && (
                <UserModal
                    user={editingUser}
                    onClose={handleCloseModal}
                    onSave={handleSaveUser}
                />
            )}
        </div>
    );
};

const UserModal = ({ user, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        password: '',
        permissions: {
            tenants: user?.permissions?.tenants || false,
            billings: user?.permissions?.billings || false,
            users: user?.permissions?.users || false,
            sales: user?.permissions?.sales || false
        },
        active: user?.active !== undefined ? user.active : true
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const submitData = { ...formData };
        if (!submitData.password && user) {
            delete submitData.password;
        }
        onSave(submitData);
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal-container">
                <div className="modal-header">
                    <h2>{user ? 'Edit Admin User' : 'Create Admin User'}</h2>
                    <button className="modal-close" onClick={onClose}>
                        <FaXmark />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>First Name</label>
                            <input
                                type="text"
                                value={formData.firstName}
                                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Last Name</label>
                            <input
                                type="text"
                                value={formData.lastName}
                                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                                required
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Phone</label>
                        <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                    </div>
                    <div className="form-group">
                        <label>Password {user && '(leave blank to keep current)'}</label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            required={!user}
                        />
                    </div>
                    {user && (
                        <div className="form-group-checkbox">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={formData.active}
                                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                                />
                                <span>Active</span>
                            </label>
                        </div>
                    )}
                    <div className="form-section">
                        <h3>Permissions</h3>
                        <div className="permissions-group">
                            <div className="form-group-checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.permissions.tenants}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            permissions: {...formData.permissions, tenants: e.target.checked}
                                        })}
                                    />
                                    <span>Tenants</span>
                                </label>
                            </div>
                            <div className="form-group-checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.permissions.billings}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            permissions: {...formData.permissions, billings: e.target.checked}
                                        })}
                                    />
                                    <span>Billings</span>
                                </label>
                            </div>
                            <div className="form-group-checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.permissions.users}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            permissions: {...formData.permissions, users: e.target.checked}
                                        })}
                                    />
                                    <span>Users</span>
                                </label>
                            </div>
                            <div className="form-group-checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.permissions.sales}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            permissions: {...formData.permissions, sales: e.target.checked}
                                        })}
                                    />
                                    <span>Sales</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="modal-actions">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary">
                            {user ? 'Update User' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const SalesScreen = ({ accessToken }) => {
    return (
        <div className="screen-content">
            <div className="screen-header">
                <h2>Sales</h2>
                <p>Sales analytics and reports</p>
            </div>
            <div className="coming-soon">
                <FaChartLine />
                <h3>Coming Soon</h3>
                <p>Sales reporting and analytics features will be available here.</p>
            </div>
        </div>
    );
};

const AdminConsole = () => {
    const [accessToken, setAccessToken] = useState('');
    const [userData, setUserData] = useState(null);
    const [permissions, setPermissions] = useState({});
    const [loginError, setLoginError] = useState('');
    const [selectedSection, setSelectedSection] = useState('');

    const handleLogin = async (email, password) => {
        setLoginError('');
        await login(
            email,
            password,
            (data) => {
                console.log('Login response:', data);
                // Extract data from the response structure: { user: { accessToken, email, ... } }
                const user = data.user;
                const token = user.accessToken;
                const perms = user.permissions || data.permissions || {};
                
                setAccessToken(token);
                setUserData(user);
                setPermissions(perms);
                
                // Set initial section based on permissions
                if (perms.tenants) setSelectedSection('tenants');
                else if (perms.billings) setSelectedSection('billings');
                else if (perms.users) setSelectedSection('users');
                else if (perms.sales) setSelectedSection('sales');
            },
            (error) => {
                setLoginError(typeof error === 'string' ? error : 'Login failed. Please try again.');
            }
        );
    };

    const handleLogout = () => {
        setAccessToken('');
        setUserData(null);
        setPermissions({});
        setSelectedSection('');
        setLoginError('');
    };

    // Show login screen if not authenticated
    if (!accessToken || !userData) {
        return (
            <div className="admin-console">
                <AdminLogin handleLogin={handleLogin} loginError={loginError} />
            </div>
        );
    }

    // Render main admin console
    return (
        <div className="admin-console">
            <AdminHeader
                userData={userData}
                selectedSection={selectedSection}
                setSelectedSection={setSelectedSection}
                permissions={permissions}
                onLogout={handleLogout}
            />
            <div className="admin-content">
                {selectedSection === 'tenants' && permissions.tenants && <TenantsScreen accessToken={accessToken} />}
                {selectedSection === 'billings' && permissions.billings && <BillingsScreen accessToken={accessToken} />}
                {selectedSection === 'users' && permissions.users && <UsersScreen accessToken={accessToken} />}
                {selectedSection === 'sales' && permissions.sales && <SalesScreen accessToken={accessToken} />}
                {!selectedSection && (
                    <div className="screen-content">
                        <div className="coming-soon">
                            <FaUserShield />
                            <h3>Welcome to Admin Console</h3>
                            <p>Please contact your administrator to grant you access permissions.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminConsole;